import { isValidOrderSideEnumStatus, OrderSideEnum } from '../../lib/enums/order-side.enum';
import { OrderStatusEnum } from '../../lib/enums/order-status.enum';
import { OrderTypeEnum } from '../../lib/enums/order-type.enum';
import lang from '../../lib/language';
import { SOROrderSide } from '../../lib/models/gateway/types';
import { formatCurrency } from '../../lib/util/DataHelpers';
import { TextHelpers } from '../../lib/util/TextHelpers';
import { convertOrderSideToText, mapOrderSORtoSide } from '../../lib/util/TradingHelpers';
import { getMaxBuyLimitPrice, getMaxSellLimitPrice, validateAggressiveBuyLimitOrder, validateAggressiveSellLimitOrder } from '../../lib/util/TradingValidationsHelper';
import { NullableNumber } from '../../lib/util/types';

export const proccessSuccessMessageTitle = (
  orderType: OrderTypeEnum, sideValue: OrderSideEnum, status?: string, hasLimitFilled?: boolean,
): string => {
  let title = lang.orderMessageTitleFilled(
    TextHelpers.getOrderTypeName(orderType), convertOrderSideToText(sideValue),
  );
  const side = isValidOrderSideEnumStatus(sideValue)
    ? sideValue
    : mapOrderSORtoSide((sideValue as any) as SOROrderSide);

  if (side === OrderSideEnum.SellShort) {
    title = lang.orderMessageTitleFilled2(
      TextHelpers.getOrderTypeName(orderType), convertOrderSideToText(side),
    );
  }

  if ((orderType === OrderTypeEnum.Limit || orderType === OrderTypeEnum.Stop) && !hasLimitFilled) {
    if (side === OrderSideEnum.SellShort) {
      title = lang.orderMessageTitlePlaced2(
        TextHelpers.getOrderTypeName(orderType), convertOrderSideToText(side),
      );
    } else {
      title = lang.orderMessageTitlePlaced(
        TextHelpers.getOrderTypeName(orderType), convertOrderSideToText(side),
      );
    }
  }

  if (status === OrderStatusEnum.PartiallyFilled) {
    title = lang.orderMessageTitlePartiallyFilled(
      TextHelpers.getOrderTypeName(orderType), convertOrderSideToText(side),
    );
  }

  return title;
};

export const processFailMessageManager = (
  isRejected: boolean,
  isExpired: boolean,
  symbol: string,
  type: OrderTypeEnum,
  sideValue: OrderSideEnum,
  quantity: number,
) => {
  const side = isValidOrderSideEnumStatus(sideValue)
    ? sideValue
    : mapOrderSORtoSide((sideValue as any) as SOROrderSide);
  switch (true) {
    case isRejected:
      if (side === OrderSideEnum.SellShort) {
        return lang.orderMessageBodyRejectedWebShort(
          TextHelpers.getOrderTypeName(type),
          convertOrderSideToText(side),
          quantity,
          symbol,
        );
      }

      return lang.orderMessageBodyRejectedWeb(
        TextHelpers.getOrderTypeName(type),
        convertOrderSideToText(side),
        quantity,
        symbol,
      );
    case isExpired: return lang.orderMessageExpired(
      TextHelpers.getOrderTypeName(type),
      convertOrderSideToText(side),
      quantity,
      symbol,
    );
    default: return lang.orderMessageTitleError(TextHelpers.getOrderTypeName(type), convertOrderSideToText(side));
  }
};

export const proccessPartiallyRejectedMessage = (
  symbol: string,
  orderType: OrderTypeEnum,
  orderSide: OrderSideEnum,
  quantity: number,
  filledSoFar?: number,
): { title: string, body: string } => ({
  title: lang.orderMessageTitlePartiallyRejectedTitle(
    TextHelpers.getOrderTypeName(orderType, true),
    convertOrderSideToText(orderSide),
  ),
  body: lang.orderMessageBodyPartiallyRejectedBody(quantity - (filledSoFar ?? 0), quantity, symbol),
});

export const proccessSuccessMessageBody = (
  symbol: string,
  status?: string,
  quantity?: number,
  orderType?: OrderTypeEnum,
  hasLimitFilled?: boolean,
  filledSoFar?: number,
): string => {
  let text = '';
  const limitOrder = orderType === OrderTypeEnum.Limit;
  const marketOrder = orderType === OrderTypeEnum.Market;
  const stopOrder = orderType === OrderTypeEnum.Stop;

  if (quantity) {
    if (marketOrder || (limitOrder && hasLimitFilled)) {
      text = lang.orderMessageBodyFilled(quantity, symbol);
    }

    if ((limitOrder || stopOrder) && !hasLimitFilled) {
      text = lang.orderMessageBodyPlaced(quantity ?? 0, symbol);
    }

    if (status === OrderStatusEnum.PartiallyFilled && filledSoFar) {
      text = lang.orderMessageBodyPartiallyFilled(filledSoFar, quantity, symbol);
    }
  }

  return text;
};

export const modifyOrderPopUpTitle = (
  orderSide: string,
  showFillOrder: boolean,
  symbol: string,
  type: OrderTypeEnum,
  status?: string,
  orderQty?: number,
): string => {
  let modifyOrderTtile = '';
  const ordSide = isValidOrderSideEnumStatus(orderSide)
    ? orderSide
    : mapOrderSORtoSide((orderSide as any) as SOROrderSide);

  const sideLabel = convertOrderSideToText(ordSide);
  if (showFillOrder) {
    const typeLabel = TextHelpers.getOrderTypeName(type);
    let filledTitle = lang.orderMessageTitleFilled(typeLabel, sideLabel);

    if (ordSide === OrderSideEnum.SellShort) {
      filledTitle = lang.orderMessageTitleFilled2(
        TextHelpers.getOrderTypeName(type), convertOrderSideToText(ordSide),
      );
    }

    modifyOrderTtile = status === OrderStatusEnum.PartiallyFilled
      ? lang.orderMessageTitlePartiallyFilled(typeLabel, sideLabel) : filledTitle;
  } else if (ordSide === OrderSideEnum.SellShort) {
    modifyOrderTtile = lang.orderMessageModifySuccessWebShort(TextHelpers.getOrderTypeName(type));
  } else {
    modifyOrderTtile = lang.orderMessageModifySuccessWeb(
      TextHelpers.getOrderTypeName(type),
      sideLabel, orderQty || 0, symbol,
    );
  }

  return modifyOrderTtile;
};

export const modifyOrderPopUpBody = (
  showFillOrder: boolean,
  symbol: string,
  filledSoFar?: number,
  status?: string,
  orderQty?: number,
) => {
  let message = '';

  if (status === OrderStatusEnum.Modified) { // successfully modified
    message = lang.orderMessageBodyModifyPending2(orderQty!, symbol);
  } else if (showFillOrder) {
    message = status === OrderStatusEnum.PartiallyFilled && filledSoFar
      ? lang.orderMessageBodyPartiallyFilled(filledSoFar, orderQty || 0, symbol)
      : lang.orderMessageBodyFilled(orderQty || 0, symbol);
  }

  return message;
};

export const validateOrder = (
  price: number,
  target: number,
  type: string,
  isBuyBtnClicked: boolean,
  setControlCheck: (check: boolean) => void,
  setErrorMessage: (message: string) => void,
) => {
  setControlCheck(true);
  setErrorMessage('');

  if (isNaN(price)) {
    setControlCheck(false);
  }
  if ([OrderTypeEnum.Limit, OrderTypeEnum.Stop].includes(type as OrderTypeEnum)) {
    if (type === OrderTypeEnum.Limit) {
      const limitCheck = isBuyBtnClicked
        ? validateAggressiveBuyLimitOrder(price, target)
        : validateAggressiveSellLimitOrder(price, target);

      if (!limitCheck) {
        const lastPrice = isBuyBtnClicked ? getMaxBuyLimitPrice(price) : getMaxSellLimitPrice(price);

        setErrorMessage(
          isBuyBtnClicked
            ? lang.commonTradingAggressiveLimitPriceError1(formatCurrency(lastPrice))
            : lang.commonTradingValidationNewOrderSellAggressiveLimitError1(formatCurrency(lastPrice)),
        );
        setControlCheck(false);
      }
    }

    if (target === 0) {
      const priceType = type === OrderTypeEnum.Limit ? lang.webTradingLimit() : lang.webTradingStop();
      setErrorMessage(lang.commonTradingPriceError(priceType));
      setControlCheck(false);
    }
  }
};

export const getValidationPrice = (
  isMarketClose: boolean,
  askPrice: NullableNumber,
  bidPrice: NullableNumber,
  tradePrice: number | null,
  isBuyOrder?: boolean,
) => {
  if (isMarketClose) {
    return tradePrice;
  }
  return isBuyOrder ? askPrice : bidPrice;
};

export const getDefaultMessageTitle = (
  symbol:string,
  sideValue:OrderSideEnum,
  orderType:OrderTypeEnum,
  orderQty:number,
  isOrderInProgress:boolean,
  isNewOrder:boolean = false,
):string => {
  if (!isOrderInProgress) {
    return '';
  }

  const side = isValidOrderSideEnumStatus(sideValue)
    ? sideValue
    : mapOrderSORtoSide((sideValue as any) as SOROrderSide);

  if (side === OrderSideEnum.SellShort) {
    if (isNewOrder) {
      return lang.orderMessageBodyPendingWeb(
        TextHelpers.getOrderTypeName(orderType, true),
        convertOrderSideToText(side),
        orderQty ?? 0,
        symbol,
      );
    }

    return lang.orderMessageBodyPendingWeb2(
      TextHelpers.getOrderTypeName(orderType, true),
      convertOrderSideToText(side),
      orderQty ?? 0,
      symbol,
    );
  }

  return lang.orderMessageBodyPendingWeb(
    TextHelpers.getOrderTypeName(orderType, true),
    convertOrderSideToText(side),
    orderQty ?? 0,
    symbol,
  );
};

export const getModifyOrderTitle = (
  sideValue:OrderSideEnum,
  orderType:OrderTypeEnum|string,
):string => {
  const side = isValidOrderSideEnumStatus(sideValue)
    ? sideValue
    : mapOrderSORtoSide((sideValue as any) as SOROrderSide);

  if (side === OrderSideEnum.SellShort) {
    return lang.orderMessageTitleModify5(TextHelpers.getOrderTypeName(orderType, true), convertOrderSideToText(side));
  }

  return lang.orderMessageTitleModify2(TextHelpers.getOrderTypeName(orderType, false), convertOrderSideToText(side));
};

export const getCancelTitle = (
  sideValue:OrderSideEnum,
  orderType:OrderTypeEnum|string,
):string => {
  const side = isValidOrderSideEnumStatus(sideValue)
    ? sideValue
    : mapOrderSORtoSide((sideValue as any) as SOROrderSide);

  if (side === OrderSideEnum.SellShort) {
    return lang.orderMessageTitleCancel2(TextHelpers.getOrderTypeName(orderType, true), convertOrderSideToText(side));
  }

  return lang.orderMessageTitleCancel(TextHelpers.getOrderTypeName(orderType, false), convertOrderSideToText(side));
};
